import React, { useState, useEffect } from 'react';
import { X, Clock, Sun, Snowflake, Info } from 'lucide-react';

// Plages horaires par défaut (correspondant à la logique Enedis standard)
const PLAGES_DEFAUT = {
  hiverSemaine: {
    pointe: [{ debut: 9, fin: 11 }, { debut: 18, fin: 20 }], // Uniquement pour CU5/LU5
    hp: [{ debut: 6, fin: 22 }],
    hc: [{ debut: 22, fin: 6 }]
  },
  hiverWeekend: {
    pointe: [],
    hp: [],
    hc: [{ debut: 0, fin: 24 }]
  },
  eteSemaine: {
    hp: [{ debut: 6, fin: 22 }],
    hc: [{ debut: 22, fin: 6 }]
  },
  eteWeekend: {
    hp: [],
    hc: [{ debut: 0, fin: 24 }]
  },
  moisHiver: [11, 12, 1, 2, 3],
  weekendToutHC: true
};

const HEURES = Array.from({ length: 25 }, (_, i) => i);

const PlageRow = ({ label, plages, onChange, color }) => {
  const addPlage = () => onChange([...plages, { debut: 0, fin: 0 }]);
  const removePlage = (idx) => onChange(plages.filter((_, i) => i !== idx));
  const updatePlage = (idx, field, val) => {
    const updated = [...plages];
    updated[idx] = { ...updated[idx], [field]: parseInt(val) };
    onChange(updated);
  };

  return (
    <div className="flex items-start gap-3 mb-2">
      <span className={`text-xs font-semibold px-2 py-1 rounded ${color} min-w-[60px] text-center mt-1`}>{label}</span>
      <div className="flex-1 space-y-1">
        {plages.map((p, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <select value={p.debut} onChange={(e) => updatePlage(idx, 'debut', e.target.value)}
              className="px-2 py-1 border border-[#BFC6CC] rounded text-sm w-20">
              {HEURES.slice(0, 24).map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select>
            <span className="text-[#687175] text-sm">→</span>
            <select value={p.fin} onChange={(e) => updatePlage(idx, 'fin', e.target.value)}
              className="px-2 py-1 border border-[#BFC6CC] rounded text-sm w-20">
              {HEURES.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select>
            <button onClick={() => removePlage(idx)} className="text-red-500 hover:text-red-700 text-xs">
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button onClick={addPlage} className="text-xs text-[#397DFF] hover:underline">+ Ajouter créneau</button>
      </div>
    </div>
  );
};

const Timeline24h = ({ config, hasPointe }) => {
  const getPlageAt = (h) => {
    if (hasPointe && config.pointe) {
      for (const p of config.pointe) {
        if (p.debut < p.fin ? (h >= p.debut && h < p.fin) : (h >= p.debut || h < p.fin)) return 'Pointe';
      }
    }
    if (config.hp) {
      for (const p of config.hp) {
        if (p.debut < p.fin ? (h >= p.debut && h < p.fin) : (h >= p.debut || h < p.fin)) return 'HP';
      }
    }
    return 'HC';
  };

  return (
    <div className="flex h-6 rounded overflow-hidden border border-[#BFC6CC] mt-2 mb-3">
      {Array.from({ length: 24 }, (_, h) => {
        const plage = getPlageAt(h);
        const colors = {
          'Pointe': 'bg-red-400',
          'HP': 'bg-orange-300',
          'HC': 'bg-blue-300'
        };
        return (
          <div key={h} className={`flex-1 ${colors[plage]} relative group cursor-default`} title={`${String(h).padStart(2, '0')}h: ${plage}`}>
            {h % 6 === 0 && <span className="absolute -bottom-4 left-0 text-[9px] text-[#687175]">{h}h</span>}
          </div>
        );
      })}
    </div>
  );
};

export const PlagesHorairesConfig = ({ pdl, formule, currentConfig, onSave, onClose }) => {
  const hasPointe = formule && (formule.includes('CU5') || formule.includes('LU5'));
  
  const [config, setConfig] = useState(() => {
    if (currentConfig && Object.keys(currentConfig).length > 0) {
      return currentConfig;
    }
    return JSON.parse(JSON.stringify(PLAGES_DEFAUT));
  });

  const updatePeriod = (period, type, plages) => {
    setConfig(prev => ({
      ...prev,
      [period]: { ...prev[period], [type]: plages }
    }));
  };

  const toggleMoisHiver = (mois) => {
    setConfig(prev => ({
      ...prev,
      moisHiver: prev.moisHiver.includes(mois)
        ? prev.moisHiver.filter(m => m !== mois)
        : [...prev.moisHiver, mois].sort((a, b) => a - b)
    }));
  };

  const MOIS_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="plages-horaires-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#BFC6CC] p-5 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-heading font-semibold text-[#191D1E] flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#397DFF]" />
              Plages horaires — PDL {pdl}
            </h2>
            <p className="text-sm text-[#687175] mt-1">Formule : {formule}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#F5F6F9] rounded-lg" data-testid="close-plages-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Info */}
          <div className="flex items-start gap-2 p-3 bg-[#E8F4FD] rounded-lg text-sm text-[#397DFF]">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Définissez les créneaux horaires HP et HC pour ce PDL. Les plages non couvertes par HP seront automatiquement considérées comme HC.</span>
          </div>

          {/* Définition des mois d'hiver */}
          <div>
            <h3 className="text-sm font-semibold text-[#191D1E] mb-2">Mois d'hiver (le reste = été)</h3>
            <div className="flex gap-1 flex-wrap">
              {MOIS_LABELS.map((label, idx) => {
                const mois = idx + 1;
                const isHiver = config.moisHiver.includes(mois);
                return (
                  <button key={mois} onClick={() => toggleMoisHiver(mois)}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      isHiver ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-orange-50 text-orange-600 border border-orange-200'
                    }`}>
                    {isHiver ? <Snowflake className="w-3 h-3 inline mr-1" /> : <Sun className="w-3 h-3 inline mr-1" />}
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Weekend */}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={config.weekendToutHC}
              onChange={(e) => setConfig(prev => ({ ...prev, weekendToutHC: e.target.checked }))}
              className="rounded" />
            <span className="text-[#191D1E]">Week-ends entièrement en HC</span>
          </label>

          {/* Hiver - Semaine */}
          <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/30">
            <h3 className="text-sm font-semibold text-blue-700 mb-3 flex items-center gap-2">
              <Snowflake className="w-4 h-4" />
              Hiver — Jours ouvrés
            </h3>
            {hasPointe && (
              <PlageRow label="Pointe" plages={config.hiverSemaine.pointe || []}
                onChange={(p) => updatePeriod('hiverSemaine', 'pointe', p)}
                color="bg-red-100 text-red-700" />
            )}
            <PlageRow label="HP" plages={config.hiverSemaine.hp}
              onChange={(p) => updatePeriod('hiverSemaine', 'hp', p)}
              color="bg-orange-100 text-orange-700" />
            <p className="text-xs text-[#687175] italic ml-[72px]">Le reste = HC</p>
            <Timeline24h config={config.hiverSemaine} hasPointe={hasPointe} />
          </div>

          {/* Hiver - Weekend (si pas tout HC) */}
          {!config.weekendToutHC && (
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/20">
              <h3 className="text-sm font-semibold text-blue-600 mb-3 flex items-center gap-2">
                <Snowflake className="w-4 h-4" />
                Hiver — Week-end
              </h3>
              <PlageRow label="HP" plages={config.hiverWeekend.hp || []}
                onChange={(p) => updatePeriod('hiverWeekend', 'hp', p)}
                color="bg-orange-100 text-orange-700" />
              <p className="text-xs text-[#687175] italic ml-[72px]">Le reste = HC</p>
              <Timeline24h config={config.hiverWeekend} hasPointe={false} />
            </div>
          )}

          {/* Été - Semaine */}
          <div className="border border-orange-200 rounded-lg p-4 bg-orange-50/30">
            <h3 className="text-sm font-semibold text-orange-700 mb-3 flex items-center gap-2">
              <Sun className="w-4 h-4" />
              Été — Jours ouvrés
            </h3>
            <PlageRow label="HP" plages={config.eteSemaine.hp}
              onChange={(p) => updatePeriod('eteSemaine', 'hp', p)}
              color="bg-orange-100 text-orange-700" />
            <p className="text-xs text-[#687175] italic ml-[72px]">Le reste = HC</p>
            <Timeline24h config={config.eteSemaine} hasPointe={false} />
          </div>

          {/* Été - Weekend (si pas tout HC) */}
          {!config.weekendToutHC && (
            <div className="border border-orange-200 rounded-lg p-4 bg-orange-50/20">
              <h3 className="text-sm font-semibold text-orange-600 mb-3 flex items-center gap-2">
                <Sun className="w-4 h-4" />
                Été — Week-end
              </h3>
              <PlageRow label="HP" plages={config.eteWeekend.hp || []}
                onChange={(p) => updatePeriod('eteWeekend', 'hp', p)}
                color="bg-orange-100 text-orange-700" />
              <p className="text-xs text-[#687175] italic ml-[72px]">Le reste = HC</p>
              <Timeline24h config={config.eteWeekend} hasPointe={false} />
            </div>
          )}

          {/* Légende */}
          <div className="flex items-center gap-4 text-xs text-[#687175]">
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-400 rounded" /> Pointe</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-orange-300 rounded" /> HP</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-300 rounded" /> HC</span>
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-white border-t border-[#BFC6CC] p-4 flex items-center justify-between">
          <button onClick={() => {
            setConfig(JSON.parse(JSON.stringify(PLAGES_DEFAUT)));
          }} className="text-sm text-[#687175] hover:text-[#191D1E]">
            Réinitialiser (plages par défaut)
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-5 py-2 border border-[#BFC6CC] rounded-lg text-sm text-[#687175] hover:bg-[#F5F6F9]">
              Annuler
            </button>
            <button onClick={() => onSave(config)} className="px-5 py-2 bg-[#397DFF] hover:bg-[#4672B6] text-white rounded-lg text-sm" data-testid="save-plages-btn">
              Enregistrer les plages
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlagesHorairesConfig;
